import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Button,
  Input,
  Space,
  message,
  Card,
  Grid,
  List,
  Form,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UserOutlined,
  TeamOutlined,
  PercentageOutlined,
  CheckCircleOutlined,
  HomeOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { DataTable, AntDColumn } from '@/components/DataTable';
import { Resident } from '@/types/resources/resident';
import ResidentFormModal from '@/components/features/resources/residents/ResidentFormModal';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import { ResidentsBulkUploadModal } from '@/components/features/resources/residents/ResidentsBulkUploadModal';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getResidents, addResident, updateResident, deleteResident, forceDeleteResident } from '@/api/residents';
import type { CreateResidentInput } from '@/api/residents';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';
import React from 'react';
import InviteDropdown from '@/components/common/InviteDropdown';
import { getUserByDocumentId } from '@/api/users';
import { 
  inviteUser, 
  generateInviteMessage, 
  generateWhatsAppUrl, 
  generateEmailUrl, 
  generateSMSUrl 
} from '@/api/invite';
import { useSimpleCRUDTour } from '@/hooks/useSimpleCRUDTour';
import { TourButton } from '@/components/common/TourButton';

const { useBreakpoint } = Grid;

// const PAGE_SIZE = 10;

export default function Residents() {
  const { t } = useTranslation(['residents', 'common']);
  const navigate = useNavigate();
  const [form] = Form.useForm<Partial<Resident> & { moveInDatePicker?: Dayjs | null }>();
  const screens = useBreakpoint();
  const queryClient = useQueryClient();
  const { firestoreUser, workspaceStats } = useAuth();
  const { startCRUDTour, isActive } = useSimpleCRUDTour();

  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openBulkUploadDialog, setOpenBulkUploadDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<Error & { dependencies?: any[]; residentName?: string } | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [lastDocs, setLastDocs] = useState<QueryDocumentSnapshot[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = React.useRef<HTMLDivElement>(null);

  // State for search
  const [searchTerm, setSearchTerm] = useState('');

  // Queries
  const { data: residentsData = { residents: [], lastDoc: undefined, total: 0 }, isLoading } = useQuery<{
    residents: Resident[];
    lastDoc?: QueryDocumentSnapshot;
    total: number;
  }>({
    queryKey: ['residents', searchTerm, currentPage, pageSize, firestoreUser?.workspace?.id],
    queryFn: async () => {
      if (!firestoreUser) {
        throw new Error('User not authenticated');
      }
      const lastDoc = currentPage > 1 ? lastDocs[currentPage - 2] : undefined;
      
      console.log('Fetching residents:', { currentPage, pageSize, lastDoc, lastDocs: lastDocs.length });
      
      return getResidents({ 
        searchTerm,
        pageSize,
        page: currentPage,
        lastDoc,
        firestoreUser 
      });
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!firestoreUser,
  });

  // Calculate resident stats from workspace stats
  const residentStats = React.useMemo(() => {
    // Get stats from workspace stats (which come from mock data)
    const totalResidents = workspaceStats?.totalResidents || 0;
    const activeResidents = Math.round(totalResidents * 0.9); // 90% active based on mock data logic
    const activeRate = totalResidents > 0 ? Math.round((activeResidents / totalResidents) * 100) : 0;
    
    const result = [
      {
        title: t('residents:stats.totalResidents'),
        value: totalResidents,
        icon: <TeamOutlined style={{ color: '#1890ff' }} />
      },
      {
        title: t('residents:stats.activeResidents'),
        value: activeResidents,
        icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />
      },
      {
        title: t('residents:stats.owners'),
        value: Math.round(totalResidents * 0.33), // 33% owners
        icon: <HomeOutlined style={{ color: '#faad14' }} />
      },
      {
        title: t('residents:stats.tenants'),
        value: Math.round(totalResidents * 0.33), // 33% tenants
        icon: <UserOutlined style={{ color: '#13c2c2' }} />
      },
      {
        title: t('residents:stats.activeRate'),
        value: `${activeRate}%`,
        icon: <PercentageOutlined style={{ color: '#eb2f96' }} />
      }
    ];
    
    return result;
  }, [workspaceStats, t]);

  // Stats slider functions
  const scrollToSlide = (index: number) => {
    if (sliderRef.current) {
      const cardWidth = 140;
      sliderRef.current.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
      setCurrentSlide(index);
    }
  };

  const handleScroll = () => {
    if (sliderRef.current) {
      const scrollLeft = sliderRef.current.scrollLeft;
      const cardWidth = 140;
      const newSlide = Math.round(scrollLeft / cardWidth);
      setCurrentSlide(newSlide);
    }
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('scroll', handleScroll);
      return () => slider.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Update lastDocs when new data arrives
  React.useEffect(() => {
    if (residentsData.lastDoc) {
      setLastDocs(prev => {
        const newLastDocs = [...prev];
        newLastDocs[currentPage - 1] = residentsData.lastDoc!;
        return newLastDocs;
      });
    }
  }, [residentsData.lastDoc, currentPage]);

  // Reset lastDocs when page size changes
  React.useEffect(() => {
    setLastDocs([]);
    setCurrentPage(1);
  }, [pageSize]);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
    setLastDocs([]);
  }, [searchTerm, pageSize]);

  // Show error message if query fails
  useEffect(() => {
    if (!residentsData.residents) {
      message.error(t('residents:messages.fetchError'));
    }
  }, [residentsData.residents, t]);

  // Mutations
  const addMutation = useMutation({
    mutationFn: (resident: CreateResidentInput) => {
      if (!firestoreUser) {
        throw new Error('No authenticated user found');
      }
      return addResident(resident, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('residents:messages.createSuccess'));
      queryClient.invalidateQueries({ queryKey: ['residents'] });
      setOpenDialog(false);
      setSelectedId(null);
    },
    onError: (error) => {
      message.error(error instanceof Error ? error.message : t('residents:messages.createError'));
    }
  });


  const deleteMutation = useMutation({
    mutationFn: async (residentId: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteResident(residentId, firestoreUser);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['residents'] });
      message.success(t('residents:messages.deleteSuccess'));
      setOpenDeleteDialog(false);
      setDeleteId(null);
    },
    onError: (error: Error) => {
      if ((error as any).dependencies) {
        setDeleteError(error);
      } else {
        message.error(error.message || t('residents:messages.deleteFailed'));
        setOpenDeleteDialog(false);
      }
    }
  });

  const forceDeleteMutation = useMutation({
    mutationFn: async (residentId: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      // Force delete will handle user account deletion if needed
      return forceDeleteResident(residentId, firestoreUser);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['residents'] });
      message.success(t('residents:messages.forceDeleteSuccess'));
      setOpenDeleteDialog(false);
      setDeleteId(null);
    },
    onError: (error: Error) => {
      message.error(error.message || t('residents:messages.forceDeleteError'));
      setOpenDeleteDialog(false);
    }
  });

  const handleRowClick = (record: Resident) => {
    navigate(`/resources/residents/${record.id}`, { state: { resident: record } });
  };

  const handleAdd = useCallback(() => {
    const initialFormValues = {
      user: {
        id: '',
        displayName: ''
      },
      type: 'owner' as const,
      moveInDatePicker: dayjs(), // For DatePicker
      documents: [],
      emergencyContact: {
        name: '',
        relationship: '',
        phone: '',
      },
      status: 'active' as const,
      notes: '',
    };
    form.resetFields();
    form.setFieldsValue(initialFormValues);
    setSelectedId(null);
    setOpenDialog(true);
  }, []);

  const handleEdit = useCallback((record: Resident) => {
    const residentToEdit = residentsData.residents.find(r => r.id === record.id);
    if (residentToEdit) {
      const formValues = {
        ...residentToEdit,
        moveInDatePicker: residentToEdit.moveInDate ? dayjs(residentToEdit.moveInDate) : null,
      };
      form.setFieldsValue(formValues);
      setSelectedId(record.id);
      setOpenDialog(true);
    }
  }, [residentsData.residents]);

  const handleDelete = useCallback((record: Resident) => {
    setDeleteId(record.id);
    setOpenDeleteDialog(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
    }
  }, [deleteId, deleteMutation]);

  
  const handleBulkUpload = () => {
    setOpenBulkUploadDialog(true);
  };

  const handleBulkUploadSuccess = () => {
    setOpenBulkUploadDialog(false);
    queryClient.invalidateQueries({ queryKey: ['residents'] });
  };

  const handleBulkUploadCancel = () => {
    setOpenBulkUploadDialog(false);
  };

  const handleSave = useCallback((values: CreateResidentInput) => {
    if (!firestoreUser) {
      message.error('User not authenticated');
      return;
    }
    
    if (selectedId) {
      // Editing existing resident - transform data to match Resident type
      const updateData: Partial<Resident> = {
        ...values,
        user: {
          id: selectedId, // Keep the existing user ID
          displayName: values.user.displayName,
          phoneNumber: values.user.phoneNumber,
          // Add other fields as needed
        }
      };
      
      updateResident(selectedId, updateData, firestoreUser).then(() => {
        message.success(t('residents:messages.updateSuccess'));
        queryClient.invalidateQueries({ queryKey: ['residents'] });
        setOpenDialog(false);
        setSelectedId(null);
      }).catch((error) => {
        message.error(error instanceof Error ? error.message : t('residents:messages.updateError'));
      });
    } else {
      // Creating new resident
      addMutation.mutate(values);
    }
  }, [selectedId, addMutation, firestoreUser, queryClient, t]);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setSelectedId(null);
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
    setLastDocs([]);
  }, []);

  const handlePageChange = useCallback((page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
  }, [pageSize]);

  const handleBulkDelete = useCallback((_: any, selectedRows: Resident[]) => {
    // TODO: Implement bulk delete functionality
    console.log('Bulk delete residents:', selectedRows);
  }, []);

  // Tour handler
  const handleStartTour = useCallback(() => {
    const hasRecords = (residentsData?.residents?.length || 0) > 0;
    const recordCount = residentsData?.total || 0;
    const firstRecordId = residentsData?.residents?.[0]?.id;

    startCRUDTour({
      pageName: 'Residents Management',
      recordType: 'resident',
      hasRecords,
      recordCount,
      firstRecordId,
      onAddRecord: handleAdd,
      onViewRecord: (id: string) => {
        const resident = residentsData?.residents?.find(r => r.id === id);
        if (resident) handleRowClick(resident);
      },
      onEditRecord: (id: string) => {
        const resident = residentsData?.residents?.find(r => r.id === id);
        if (resident) handleEdit(resident);
      },
      onDeleteRecord: (id: string) => {
        const resident = residentsData?.residents?.find(r => r.id === id);
        if (resident) handleDelete(resident);
      }
    });
  }, [startCRUDTour, residentsData, handleAdd, handleRowClick, handleEdit, handleDelete]);

  const handleInvite = async (resident: Resident, method: 'whatsapp' | 'email' | 'sms') => {
    if (!firestoreUser || !resident.user?.id) {
      message.error('User not authenticated or missing user reference');
      return;
    }

    try {
      // Get user details
      const user = await getUserByDocumentId(resident.user.id, firestoreUser);
      if (!user) {
        message.error('User not found');
        return;
      }

      // Validate contact information
      if (!user.email) {
        message.error('User must have an email address to be invited');
        return;
      }

      if (method === 'whatsapp' && !user.phoneNumber) {
        message.error('User must have a phone number for WhatsApp invitation');
        return;
      }

      // Invite the user (creates invitation record)
      const inviteResult = await inviteUser(user, method, firestoreUser);
      
      if (!inviteResult.success) {
        message.error(inviteResult.message);
        return;
      }

      // Generate appropriate invite message
      const inviteMessage = generateInviteMessage(
        user,
        firestoreUser.workspace?.name || 'Unknown Workspace',
        inviteResult.inviteUrl || ''
      );

      // Open appropriate invite method
      if (method === 'whatsapp' && user.phoneNumber) {
        const whatsappUrl = generateWhatsAppUrl(user.phoneNumber, inviteMessage);
        window.open(whatsappUrl, '_blank');
      } else if (method === 'email') {
        const emailUrl = generateEmailUrl(
          user.email,
          `Invitation to join ${firestoreUser.workspace?.name || 'Unknown Workspace'}`,
          inviteMessage
        );
        window.open(emailUrl, '_blank');
      } else if (method === 'sms' && user.phoneNumber) {
        const smsUrl = generateSMSUrl(user.phoneNumber, inviteMessage);
        window.open(smsUrl, '_blank');
      }

      // Update resident invite status
      await updateResident(resident.id, { invited: true }, firestoreUser);
      
      // Show success message
      message.success(inviteResult.message);
      
    } catch (error) {
      console.error('Error inviting user:', error);
      message.error(error instanceof Error ? error.message : 'Failed to invite user');
    }
  };

  // const total = residentsData?.total ?? 0;

  const residentColumns: AntDColumn<Resident>[] = useMemo(() => [
    {
      title: t('residents:fields.user'),
      dataIndex: 'user' as keyof Resident,
      key: 'user',
      render: (_: any, record: Resident) => record.user?.displayName || record.id,
    },
    {
      title: t('residents:fields.phone'),
      dataIndex: 'user' as keyof Resident,
      key: 'phone',
      render: (_: any, record: Resident) => record.user?.phoneNumber || '-',
    },
    {
      title: t('residents:fields.unit'),
      dataIndex: 'apartmentUnit' as keyof Resident,
      key: 'unit',
      render: (_: any, record: Resident) => record.apartmentUnit?.number || '-',
    },
    {
      title: t('residents:fields.type'),
      dataIndex: 'type' as keyof Resident,
      key: 'type',
      render: (type: string) => t(`residents:residencyTypes.${type}`),
    },
    {
      title: t('residents:fields.status'),
      dataIndex: 'status' as keyof Resident,
      key: 'status',
      render: (status: string) => t(`residents:status.${status}`),
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      render: (_: any, record: Resident) => (
        <Space>
          <InviteDropdown
            name={record.user?.displayName || ''}
            onInvite={(method) => handleInvite(record, method)}
          />
        </Space>
      ),
    },
  ], [t, handleInvite]);

  const renderResidentCard = useCallback((resident: Resident) => (
    <Card
      key={resident.id}
      hoverable
      style={{ marginBottom: 16 }}
      actions={[
        <Button
          key="view"
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleRowClick(resident)}
        >
          {t('common:actions.view')}
        </Button>,
        <Button
          key="edit"
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(resident)}
        >
          {t('common:actions.edit')}
        </Button>,
        <Button
          key="delete"
          type="link"
          icon={<DeleteOutlined />}
          danger
          onClick={() => handleDelete(resident)}
        >
          {t('common:actions.delete')}
        </Button>,
      ]}
    >
      <Card.Meta
        title={resident.user?.displayName || resident.id}
        description={
          <Space direction="vertical" size="small">
            <div>{t('residents:fields.phone')}: {resident.user?.phoneNumber || '-'}</div>
            <div>{t('residents:fields.unit')}: {resident.apartmentUnit?.number || '-'}</div>
            <div>{t('residents:fields.type')}: {t(`residents:residencyTypes.${resident.type}`)}</div>
            <div>{t('residents:fields.status')}: {t(`residents:status.${resident.status}`)}</div>
          </Space>
        }
      />
    </Card>
  ), [t, handleRowClick, handleEdit, handleDelete]);

  return (
    <div style={{ width: '100%' }}>
      {/* Enhanced Stats Section */}
      <div style={{ marginBottom: 24 }}>
        <div
          ref={sliderRef}
          style={{
            display: 'flex',
            overflowX: screens.xs ? 'auto' : 'visible',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            gap: screens.xs ? '12px' : '8px',
            padding: '8px 0',
            flexWrap: screens.xs ? 'nowrap' : 'wrap',
            justifyContent: screens.xs ? 'flex-start' : 'center'
          }}
        >
          {residentStats.map((stat, idx) => (
            <Card
              key={idx}
              style={{
                minWidth: 120,
                maxWidth: 150,
                width: '100%',
                flex: screens.xs ? '0 0 auto' : '1 1 0',
                cursor: 'pointer',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                border: '1px solid #f0f0f0',
                margin: screens.xs ? 0 : '0 4px'
              }}
              onClick={() => scrollToSlide(idx)}
              hoverable
              styles={{
                body: {
                  padding: '16px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }
              }}
            >
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                marginBottom: '4px'
              }}>
                <div style={{ fontSize: '20px' }}>
                  {stat.icon}
                </div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: '700',
                  color: '#262626'
                }}>
                  {stat.value}
                </div>
              </div>
              <div style={{ 
                fontSize: '14px', 
                color: '#262626',
                textAlign: 'center',
                lineHeight: '1.2',
                fontWeight: '600'
              }}>
                {stat.title}
              </div>
            </Card>
          ))}
        </div>
        
        {/* Progress Indicators for Mobile */}
        {screens.xs && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '6px', 
            marginTop: '12px' 
          }}>
            {residentStats.map((_, idx) => (
              <div
                key={idx}
                onClick={() => scrollToSlide(idx)}
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: currentSlide === idx ? '#1890ff' : '#d9d9d9',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease'
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Tour Button */}
      <div style={{ marginBottom: 16, textAlign: 'center' }}>
        <TourButton onClick={handleStartTour} disabled={isActive} size="large" />
      </div>

      <Space direction="vertical" style={{ width: '100%' }}>
        {screens.xs ? (
          <div style={{ marginBottom: 16 }}>
            <Input.Search
              id="residents-search-input"
              placeholder={t('common:search.placeholder')}
              allowClear
              onSearch={handleSearch}
              style={{ width: '100%', marginBottom: 8 }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                icon={<UploadOutlined />}
                onClick={handleBulkUpload}
                style={{ flex: 1 }}
              >
                {t('common:actions.bulkUpload')}
              </Button>
              <Button
                id="residents-add-button"
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
                style={{ flex: 1 }}
              >
                {t('common:actions.create')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-center mb-4">
            <Space>
              <Input.Search
                id="residents-search-input"
                className="search-input"
                placeholder={t('common:search.placeholder')}
                allowClear
                onSearch={handleSearch}
                style={{ width: 200 }}
              />
              <Button
                icon={<UploadOutlined />}
                onClick={handleBulkUpload}
              >
                {t('common:actions.bulkUpload')}
              </Button>
              <Button
                id="residents-add-button"
                className="add-record-button"
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                {t('common:actions.create')}
              </Button>
            </Space>
          </div>
        )}
        {screens.xs ? (
          <List
            dataSource={residentsData.residents || []}
            renderItem={renderResidentCard}
            rowKey="id"
            grid={{ gutter: 16, xs: 1, sm: 1 }}
            loading={isLoading}
          />
        ) : (
          <Card className="data-table-card">
            <DataTable
              className="data-table"
              dataSource={residentsData.residents || []}
              columns={residentColumns}
              loading={isLoading}
              rowKey="id"
              onEdit={handleEdit}
              onDelete={handleDelete}
              onView={handleRowClick}
              onBulkDelete={handleBulkDelete}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: residentsData.total,
                onChange: handlePageChange,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ['10', '25', '50', '100'],
                showTotal: (total: number, range: [number, number]) => 
                  t('common:pagination.showing', { 
                    start: range[0], 
                    end: range[1], 
                    total 
                  })
              }}
            />
          </Card>
        )}
      </Space>

      <ResidentFormModal
        visible={openDialog}
        onCancel={handleCloseDialog}
        onSubmit={handleSave}
        initialValues={selectedId ? residentsData.residents.find(r => r.id === selectedId) : undefined}
        title={selectedId ? t('residents:actions.edit') : t('residents:actions.add')}
      />

      <DeleteConfirmationModal
        visible={openDeleteDialog}
        onCancel={() => {
          setOpenDeleteDialog(false);
          setDeleteError(undefined);
        }}
        onConfirm={handleConfirmDelete}
        onForceDelete={() => {
          if (deleteId) {
            forceDeleteMutation.mutate(deleteId);
          }
        }}
        title={t('common:confirmDeleteTitle')}
        error={deleteError}
        onErrorDismiss={() => setDeleteError(undefined)}
        isDeleting={deleteMutation.isPending || forceDeleteMutation.isPending}
      />

      <ResidentsBulkUploadModal
        visible={openBulkUploadDialog}
        onCancel={handleBulkUploadCancel}
        onSuccess={handleBulkUploadSuccess}
      />
    </div>
  );
} 